import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import DropDown from "@components/ui/dropdown/DropDown";
import { useProjectStore } from "@store/projectStore";
import Button from "@components/ui/buttons/Button";
import { useState, useRef, KeyboardEvent, useEffect } from "react";
import AreaTextField from "@components/ui/textfields/AreaTextField";
import TextField from "@components/ui/textfields/TextField";
import { colors } from "sexy";

import {
  Org,
  User,
  EmailSent,
  ControlPoint,
  Project,
  EmailTemplate,
  ProjectHistoryEvent,
} from "types";
import { handleFetchEmailOptions, fillVariablesInEmailTemplate } from "helpers/emailHelpers";
import { CustomImage } from "@components/ui/images/CustomImage";
import { getFlutterTime } from "@services/timeHelpers";
import { showNotification } from "context/notification.service";
import { projectsApi } from "@services/api/projects";

interface EmailChipProps {
  email: string;
  onRemove: () => void;
}

const EmailChip = ({ email, onRemove }: EmailChipProps) => (
  <div className="inline-flex items-center bg-neutral-100 bg-primary-100 text-primary-700 px-3 gap-2 py-0.5 rounded-full text-sm mr-1 mb-1">
    <span>{email}</span>
    <button
      onClick={onRemove}
      className="hover:bg-primary-200 rounded-full p-0.5 transition-colors"
      type="button"
    >
      <Icon1 name="effacer2" size={18} />
    </button>
  </div>
);

interface EmailInputFieldProps {
  label: string;
  emails: string[];
  onAddEmail: (email: string) => void;
  onRemoveEmail: (index: number) => void;
  setEmails: (emails: string[]) => void;
  placeholder?: string;
  emailOptions: { label: string; value: string }[];
  setEmailOptions: (options: { label: string; value: string }[]) => void;
}

const EmailInputField = ({
  label,
  emails,
  onAddEmail,
  onRemoveEmail,
  setEmails,
  emailOptions,
  setEmailOptions,
  placeholder = "Entrez une adresse email",
}: EmailInputFieldProps) => {
  const [inputValue, setInputValue] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const handleAddEmail = (email: string) => {
    const trimmedEmail = email.trim();
    if (
      trimmedEmail &&
      validateEmail(trimmedEmail) &&
      !emails.includes(trimmedEmail)
    ) {
      onAddEmail(trimmedEmail);
      setInputValue("");
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "," || e.key === ";") {
      e.preventDefault();
      handleAddEmail(inputValue);
    } else if (
      e.key === "Backspace" &&
      inputValue === "" &&
      emails.length > 0
    ) {
      onRemoveEmail(emails.length - 1);
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const handleBlur = () => {
    setTimeout(() => setShowSuggestions(false), 200);
    if (inputValue.trim()) {
      handleAddEmail(inputValue);
    }
  };

  const filteredOptions = emailOptions.filter(
    (opt) =>
      !emails.includes(opt.value) &&
      (inputValue === "" ||
        opt.label.toLowerCase().includes(inputValue.toLowerCase()) ||
        opt.value.toLowerCase().includes(inputValue.toLowerCase())),
  );

  return (
    <div className="flex flex-row items-start w-full">
      <div className="min-w-100 pt-2 text-neutral-600">{label}</div>
      <div className="relative w-full">
        <div
          className="w-full border-b-1 border-neutral-300 cursor-text min-h-[32px] flex flex-wrap items-center"
          onClick={() => inputRef.current?.focus()}
        >
          {emails.map((email, index) => (
            <EmailChip
              key={index}
              email={email}
              onRemove={() => onRemoveEmail(index)}
            />
          ))}
          <input
            ref={inputRef}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setShowSuggestions(true);
            }}
            onFocus={() => setShowSuggestions(true)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            placeholder={emails.length === 0 ? placeholder : ""}
            className="flex-1 min-w-[150px] border-0 outline-none py-1 text-h2"
          />
        </div>
        {showSuggestions && filteredOptions.length > 0 && (
          <div className="absolute top-full left-0 w-full z-50 bg-white border rounded-lg shadow-lg mt-1 max-h-200 overflow-y-auto">
            {filteredOptions.map((option, index) => (
              <div
                key={index}
                className="px-3 py-2 text-h3 hover:bg-neutral-50 cursor-pointer transition-colors"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleAddEmail(option.value);
                  setShowSuggestions(false);
                }}
              >
                <div className="text-black">{option.value}</div>
                <div className="text-neutral-400 text-h4">{option.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export interface EmailOption {
  label: string;
  value: string;
}

interface EmailModalProps {
  onClose: () => void;
  email_template_id?: string;
  controlpoint?: ControlPoint;
  refreshControlPoint?: () => void;
  activeProject?: Project;
  setActiveProject?: (project: Project) => void;
  userData?: User;
  orgData?: Org;
  sentEmails?: EmailSent[];
  setSentEmails?: (emails: EmailSent[]) => void;
}

export const EmailModal = ({
  onClose,
  email_template_id,
  controlpoint,
  refreshControlPoint,
  activeProject,
  setActiveProject,
  userData,
  orgData,
  sentEmails = [],
  setSentEmails,
}: EmailModalProps) => {
  const fetchSentEmails = useProjectStore((state) => state.fetchSentEmails);
  const emailTemplates = useProjectStore((state) => state.emailTemplates);
  const libraryDocuments = useProjectStore((state) => state.libraryDocuments);

  const initialTemplateId = email_template_id || emailTemplates[0]?.id || "";
  const initialTemplate = emailTemplates.find((m) => m.id === initialTemplateId);
  const initialFilled =
    initialTemplate && activeProject && userData && orgData
      ? fillVariablesInEmailTemplate(initialTemplate as EmailTemplate, activeProject, userData, orgData)
      : null;

  const [model, setModel] = useState<string>(initialTemplateId);
  const [showCC, setShowCC] = useState<boolean>(false);
  const [showBCC, setShowBCC] = useState<boolean>(false);

  const [toEmails, setToEmails] = useState<string[]>([]);
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [bccEmails, setBccEmails] = useState<string[]>([]);
  const [emailOptions, setEmailOptions] = useState<EmailOption[]>([]);

  const [subject, setSubject] = useState<string>(
    initialFilled?.subject || initialTemplate?.subject || "",
  );
  const [content, setContent] = useState<string>(
    initialFilled?.body || initialTemplate?.body || "",
  );
  const [photos, setPhotos] = useState<string[]>([]);
  const [documents, setDocuments] = useState<string[]>(
    emailTemplates.find((m) => m.id === model)?.library_documents_id || [],
  );
  const [addPhotoModalOpen, setAddPhotoModalOpen] = useState(false);

  const addToEmail = (email: string) => setToEmails([...toEmails, email]);
  const removeToEmail = (index: number) =>
    setToEmails(toEmails.filter((_, i) => i !== index));

  const addCcEmail = (email: string) => setCcEmails([...ccEmails, email]);
  const removeCcEmail = (index: number) =>
    setCcEmails(ccEmails.filter((_, i) => i !== index));

  const addBccEmail = (email: string) => setBccEmails([...bccEmails, email]);
  const removeBccEmail = (index: number) =>
    setBccEmails(bccEmails.filter((_, i) => i !== index));

  useEffect(() => {
    if (activeProject) {
      const options = handleFetchEmailOptions(activeProject);
      setEmailOptions(options);
    }
  }, [activeProject]);

  const handleModelChange = (value: string) => {
    setModel(value);

    const selectedModel = emailTemplates.find((m) => m.id === value);
    if (selectedModel && activeProject && userData && orgData) {
      const filled = fillVariablesInEmailTemplate(
        selectedModel as EmailTemplate,
        activeProject,
        userData,
        orgData,
      );

      setSubject(filled.subject || "");
      setContent(filled.body || "");
      setDocuments(selectedModel.library_documents_id || []);
    }
  };

  const handleSend = async () => {
    if (!activeProject || !userData || !orgData) return;

    const tempId = "temp_id_" + Date.now();

    try {
      // Backend now handles email sending, history event creation, and controlpoint update
      const updatedProject = await projectsApi.sendEmailWithHistory(
        activeProject.id,
        {
          id: tempId,
          subject: subject,
          body: content,
          org_id: orgData.id,
          project_id: activeProject.id,
          sender_user_id: userData.id,
          email_template_id: model,
          emails_to: toEmails,
          emails_cc: ccEmails,
          emails_bcc: bccEmails,
          files: libraryDocuments
            .filter((doc) => documents.includes(doc.id))
            .map((doc) => doc.file),
          photos_id: photos,
          controlpoint_id: controlpoint?.id,
        }
      );

      // Refresh sent emails list
      await fetchSentEmails(orgData.id, activeProject.id);

      // Update local state with the new project
      if (setActiveProject) {
        setActiveProject(updatedProject);
      }

      if (refreshControlPoint) {
        refreshControlPoint();
      }

      showNotification({
        type: "success",
        message: `Email envoyé à ${toEmails.join(", ")}`,
      });

      onClose();
    } catch (error) {
      console.error("Error sending email:", error);
      showNotification({
        type: "error",
        message: "Erreur lors de l'envoi de l'email",
      });
    }
  };

  const selectedModel = emailTemplates.find((m) => m.id === model);
  const isSendDisabled =
    (selectedModel?.mandatory_photo && photos.length === 0) ||
    toEmails.length === 0;

  return (
    <BaseModal
      width="max-w-800"
      title="Envoyer un email"
      startIcon={<Icon1 name="email" size={20} />}
      onClose={onClose}
    >
      {/* TODO: Add EmailJoinPhotoModal component here when created */}
      {/* {addPhotoModalOpen && (
        <EmailJoinPhotoModal
          onClose={() => setAddPhotoModalOpen(false)}
          onAddPhotosToEmail={(photoIDs: string[]) => {
            setPhotos(photoIDs);
          }}
          photos={photos}
        />
      )} */}

      <div className="bg-white flex flex-col gap-2 p-4">
        <DropDown
          options={emailTemplates.map((r) => ({
            value: r.id,
            label: r.name,
          }))}
          label="Modèle d'email"
          value={model}
          onSelect={(value) => handleModelChange(value as string)}
        />

        <div className="flex flex-col text-h2 py-3 gap-2">
          {/* From */}
          <div className="flex flex-row items-center">
            <div className="min-w-100 text-neutral-600">De</div>
            <div className="w-full py-2 font-medium border-b-1">
              {orgData?.infos?.sender_email || "email@example.com"}
            </div>
          </div>

          {/* To */}
          <div className="flex flex-col">
            <div className="flex flex-row items-start">
              <EmailInputField
                label="Vers"
                emails={toEmails}
                onAddEmail={addToEmail}
                setEmails={setToEmails}
                onRemoveEmail={removeToEmail}
                placeholder="Ajouter des destinataires"
                emailOptions={emailOptions}
                setEmailOptions={setEmailOptions}
              />
              <div className="ml-2 flex flex-row gap-2 pt-2">
                {!showCC && (
                  <button
                    className="text-sm px-2 py-1 rounded transition-colors text-neutral-500 hover:bg-neutral-100"
                    onClick={() => setShowCC(true)}
                    type="button"
                  >
                    CC
                  </button>
                )}
                {!showBCC && (
                  <button
                    className="text-sm px-2 py-1 rounded transition-colors text-neutral-500 hover:bg-neutral-100"
                    onClick={() => setShowBCC(true)}
                    type="button"
                  >
                    BCC
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* CC */}
          {showCC && (
            <EmailInputField
              label="CC"
              emails={ccEmails}
              onAddEmail={addCcEmail}
              setEmails={setCcEmails}
              onRemoveEmail={removeCcEmail}
              placeholder="Copie"
              emailOptions={emailOptions}
              setEmailOptions={setEmailOptions}
            />
          )}

          {/* BCC */}
          {showBCC && (
            <EmailInputField
              label="BCC"
              emails={bccEmails}
              onAddEmail={addBccEmail}
              setEmails={setBccEmails}
              onRemoveEmail={removeBccEmail}
              placeholder="Copie cachée"
              emailOptions={emailOptions}
              setEmailOptions={setEmailOptions}
            />
          )}

          {/* Subject */}
          <div className="flex flex-row items-center mt-5">
            <div className="min-w-100 text-neutral-600">Sujet</div>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full border-0 border-b-1 border-neutral-300 rounded-none py-2 outline-none focus:border-primary-500 transition-colors"
            />
          </div>
        </div>

        <AreaTextField
          label="Contenu"
          rows={12}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={`Bonjour,

Dans le cadre du suivi de l'opération, nous avons procédé à la vérification des factures transmises par les artisans au regard des devis validés.

Nous vous remercions de bien vouloir corriger la facture concernée afin qu'elle soit conforme au devis contractuel, puis de nous la transmettre à nouveau pour validation.

Nous restons à votre disposition pour toute précision complémentaire et pour échanger si nécessaire sur ce point.

Cordialement,

David Comy
Bureau Gilbert Comy`}
        />

        {/* Photos Preview */}
        {photos.length > 0 && (
          <div
            className="flex flex-row gap-1.5 mt-2 flex-wrap items-center p-2 bg-neutral-50 rounded-lg cursor-pointer"
            onClick={() => setAddPhotoModalOpen(true)}
          >
            {activeProject?.photos
              ?.filter((p) => photos.includes(p.id))
              .map((photo, index) => (
                <CustomImage
                  key={index}
                  source={{ uri: photo.file.url }}
                  alt={`Photo ${index + 1}`}
                  className="w-20 h-20 rounded-lg"
                  hidden={photo.hidden}
                />
              ))}
          </div>
        )}

        {/* Mandatory Photo Warning */}
        {selectedModel?.mandatory_photo && photos.length === 0 && (
          <div
            className="w-full flex flex-row p-2.5 rounded-lg border-2 justify-center cursor-pointer"
            style={{
              backgroundColor: colors.red_100,
              borderColor: colors.red_300,
            }}
            onClick={() => setAddPhotoModalOpen(true)}
          >
            <div className="text-sm" style={{ color: colors.red_500 }}>
              Ce modèle d'email nécessite l'ajout d'au moins une photo.
            </div>
          </div>
        )}

        {/* Document Attachments */}
        {documents.length > 0 && (
          <div className="flex flex-col gap-1.5 mt-2">
            {libraryDocuments
              ?.filter((doc) => documents.includes(doc.id))
              .map((doc, index) => (
                <div
                  key={index}
                  className="flex flex-row gap-2 items-center p-2 bg-neutral-50 rounded-lg"
                >
                  <Icon1 name="file" size={20} color={colors.neutral_400} />
                  <div
                    className="flex-1 text-sm truncate"
                    style={{ color: colors.neutral_500 }}
                  >
                    {doc.file.name}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>
          Annuler
        </SecondaryButton>
        <SecondaryButton
          variant="small"
          onClick={() => setAddPhotoModalOpen(true)}
        >
          <>
            Joindre
            <Icon1 name="join" size={18} />
          </>
        </SecondaryButton>
        <Button
          variant="small"
          label="Envoyer"
          onClick={handleSend}
          disabled={isSendDisabled}
        />
      </div>
    </BaseModal>
  );
};
